import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, Table } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import { format } from 'date-fns';
import './salesReport.css';

const SalesReport = () => {
  const navigate = useNavigate();

  // ====== Lab / Admin Logic ======
  const [labId, setLabId] = useState(null);
  const [userType, setUserType] = useState('lab');

  const [showParam, setShowParam] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountsMap, setAccountsMap] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    start_date: '2026-09-01',
    end_date: '2026-09-30',
    reps: [],
    salesReps: 'All',
    procedureFilterType: 'include',
    procedures: [],
    procedureTags: 'No filter',
    displayProcedureColumns: false,
    excludeAlloys: false,
    includeClosed: false,
    accountTags: 'No filter',
    includeFinanceCharge: false,
    formatType: 'On Screen'
  });
  const [selectedAccountOptions, setSelectedAccountOptions] = useState([]);

  // Mock procedures for UI representation
  const procedureListOptions = [
    '3D PRINTED MODELS',
    '3D PRINTED MODEL (HORSE SHOE SHAPE)',
    '60% PRECIOUS GOLD CROWN',
    '8-14 ALIGNERS (DUAL ARCH)',
    '8-14 ALIGNERS (SINGLE ARCH)',
    'ADIN - guided surgery sleeves 2mm',
    'ADIN - guided surgery sleeves 5.5mm',
    'ALVEOPLASTY CLEAR GUIDE',
    'Anchor pin - Surgical Guide',
    'Anchor Twist Drill - Surgical guide'
  ];

  // Load userLabId & userType from localStorage
  useEffect(() => {
    const storedLabId = localStorage.getItem('userLabId');
    const storedUserType = localStorage.getItem('userType');

    if (storedLabId) setLabId(storedLabId);
    if (storedUserType) {
      setUserType(storedUserType);
    } else if (storedLabId) {
      setUserType('lab');
    } else {
      setUserType('admin');
    }
  }, []);

  // Fetch Accounts with Lab vs Admin Filtering
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        let fetchedAccounts = [];

        if (userType === 'lab' && labId) {
          const response = await axios.get(
            `https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${labId}`
          );
          if (response.data.success) {
            fetchedAccounts = response.data.data || [];
          }
        } else {
          const [hmsRes, ddxRes] = await Promise.all([
            axios.get("https://hms.backend.hiragroupcloud.com/api/account/account"),
            axios.get("https://hms.backend.hiragroupcloud.com/api/users")
          ]);

          const hmsAccounts = (hmsRes.data.data || []).map(acc => ({
            accountId: acc.accountId || acc._id,
            account_name: acc.account_name || "Unnamed Account"
          }));

          const ddxAccounts = (ddxRes.data.users || []).map(user => ({
            accountId: user._id,
            account_name: user.practiceName || `${user.firstName} ${user.lastName}`
          }));

          fetchedAccounts = [...hmsAccounts, ...ddxAccounts];
        }

        const options = fetchedAccounts
          .filter(acc => acc && acc.account_name)
          .map(acc => ({
            value: acc.accountId,
            label: acc.account_name.trim()
          }));

        const map = fetchedAccounts.reduce((obj, acc) => {
          obj[acc.accountId] = acc.account_name.trim();
          return obj;
        }, {});

        setAccountOptions(options);
        setAccountsMap(map);
      } catch (err) {
        console.error("Error fetching accounts:", err);
        setError('Could not fetch accounts. Please try again later.');
      }
    };

    if (userType && (userType === 'admin' || labId)) {
      fetchAccounts();
    }
  }, [userType, labId]);

  const runReport = async (params) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('https://hms.backend.hiragroupcloud.com/api/invoices/sales-report', {
        params: {
          start_date: params.start_date,
          end_date: params.end_date,
          reps: params.reps?.includes('') ? [] : params.reps,
        },
      });

      const totalSales = response.data.summary.totalSales;

      const enhancedData = {
        ...response.data,
        details: response.data.details.map(detail => ({
          ...detail,
          account: accountsMap[detail.accountId] || `Unknown Account (ID: ${detail.accountId})`,
          percentage: totalSales > 0 ? ((detail.sales / totalSales) * 100).toFixed(2) + '%' : '0%',
          invoices: detail.invoices.map(invoice => ({
            ...invoice,
            accountName: accountsMap[invoice.accountId] || `Unknown Account (ID: ${invoice.accountId})`,
            percentage: totalSales > 0 ? ((invoice.amount / totalSales) * 100).toFixed(2) + '%' : '0%'
          }))
        }))
      };

      setReportData(enhancedData);
      setShowParam(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate report. Please try again.');
      console.error('Report error:', err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const handleReset = () => {
    setShowParam(true);
    setReportData(null);
    setError(null);
    setFormData({
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date().toISOString().split('T')[0],
      reps: [],
      salesReps: 'All',
      procedureFilterType: 'include',
      procedures: [],
      procedureTags: 'No filter',
      displayProcedureColumns: false,
      excludeAlloys: false,
      includeClosed: false,
      accountTags: 'No filter',
      includeFinanceCharge: false,
      formatType: 'On Screen'
    });
    setSelectedAccountOptions([]);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleProcedureSelection = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({ ...prev, procedures: selectedOptions }));
  };

  const handleSelectChange = (selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setSelectedAccountOptions(selectedOptions || []);
    setFormData(prev => ({ ...prev, reps: values }));
  };

  const handleCaseLinkClick = (caseId) => {
    navigate(`/case/${caseId}`);
  };

  const handleAccountLinkClick = (accountId, accountName) => {
    navigate(`/tabsWorking/${encodeURIComponent(accountId)}`, {
      state: { accountName }
    });
  };

  const handleInvoiceLinkClick = (invoiceNumber) => {
    navigate(`/invoices/${invoiceNumber}`);
  };

  return (
    <div className="sales-report-container">

      {/* Header when report is available */}
      {reportData && (
        <div className="report-header">
          <h3 className="report-header-title">
            {`Sales Report: ${formatDate(reportData.summary.startDate)} to ${formatDate(reportData.summary.endDate)}`}
          </h3>
          <div className="report-header-actions">
            <button onClick={handleReset} className="btn-reset">
              Reset
            </button>
            <button onClick={() => setShowParam(!showParam)} className="btn-toggle-param">
              {showParam ? 'Hide Parameters' : 'Parameters'}
            </button>
          </div>
        </div>
      )}

      {/* Parameters Section */}
      {showParam && (
        <div className="param-container">

          {/* Header Title with underline */}
          <div className="param-title-wrapper">
            <h2 className="param-title">
              Parameters
            </h2>
          </div>

          {/* Light Gray Form Box Container */}
          <div className="param-form-box">
            <form onSubmit={(e) => { e.preventDefault(); runReport(formData); }}>
              <div className="param-grid">

                {/* Start Date */}
                <label className="param-label">
                  Start Date <span className="required-star">*</span>
                </label>
                <div>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    required
                    className="param-input param-input-date"
                  />
                </div>

                {/* End Date */}
                <label className="param-label">
                  End Date <span className="required-star">*</span>
                </label>
                <div>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    required
                    className="param-input param-input-date"
                  />
                </div>

                {/* Sales Reps */}
                <label className="param-label">Sales Reps</label>
                <div>
                  <select
                    name="salesReps"
                    value={formData.salesReps}
                    onChange={handleInputChange}
                    className="param-input param-input-select"
                  >
                    <option value="All">All</option>
                  </select>
                </div>

                {/* Procedures Radio Buttons & Multi-select Box */}
                <label className="param-label param-label-sm">Procedures</label>
                <div>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="procedureFilterType"
                        value="include"
                        checked={formData.procedureFilterType === 'include'}
                        onChange={handleInputChange}
                        className="radio-input"
                      />
                      Include
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="procedureFilterType"
                        value="exclude"
                        checked={formData.procedureFilterType === 'exclude'}
                        onChange={handleInputChange}
                        className="radio-input"
                      />
                      Exclude
                    </label>
                  </div>

                  {/* Multi-Select Listbox */}
                  <select
                    multiple
                    value={formData.procedures}
                    onChange={handleProcedureSelection}
                    className="multi-select-box"
                  >
                    {procedureListOptions.map((proc, index) => (
                      <option key={index} value={proc} className="multi-select-option">
                        {proc}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Procedure Tags */}
                <label className="param-label">Procedure Tags</label>
                <div>
                  <select
                    name="procedureTags"
                    value={formData.procedureTags}
                    onChange={handleInputChange}
                    className="param-input param-input-select"
                  >
                    <option value="No filter">No filter</option>
                  </select>
                </div>

                {/* Options Checkboxes */}
                <label className="param-label param-label-sm">Options</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="displayProcedureColumns"
                      checked={formData.displayProcedureColumns}
                      onChange={handleInputChange}
                      className="checkbox-input"
                    />
                    Display procedure columns
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="excludeAlloys"
                      checked={formData.excludeAlloys}
                      onChange={handleInputChange}
                      className="checkbox-input"
                    />
                    Exclude Alloys and Materials
                  </label>
                </div>

                {/* Format Dropdown */}
                <label className="param-label">
                  Format <span className="required-star">*</span>
                </label>
                <div>
                  <select
                    name="formatType"
                    value={formData.formatType}
                    onChange={handleInputChange}
                    className="param-input param-input-format"
                  >
                    <option value="On Screen">On Screen</option>
                    <option value="PDF">PDF</option>
                    <option value="Excel">Excel</option>
                  </select>
                </div>

              </div>

              {/* Submit / Run Button */}
              <div className="submit-wrapper">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-run"
                >
                  {loading ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      Run
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-box">
          <Spinner animation="border" className="spinner-blue" />
          <p className="loading-text">Generating report...</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-box">
          {error}
        </div>
      )}

      {/* Report Results */}
      {reportData && (
        <div className="results-container">
          <div className="summary-wrapper">
            <h5 className="section-title">Summary</h5>
            <div className="summary-grid">
              <div className="summary-card">
                <div className="summary-card-title">Total Sales</div>
                <div>£{reportData.summary.totalSales.toFixed(2)}</div>
              </div>
              <div className="summary-card">
                <div className="summary-card-title">Number of Invoices</div>
                <div>{reportData.summary.invoiceCount}</div>
              </div>
              <div className="summary-card">
                <div className="summary-card-title">Number of Accounts</div>
                <div>{reportData.summary.accountCount}</div>
              </div>
            </div>
          </div>

          <h5 className="section-title">Details</h5>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th className="table-header-cell">Account</th>
                <th className="table-header-cell-right">Sales</th>
                <th className="table-header-cell-right">% of Sales</th>
              </tr>
            </thead>
            <tbody>
              {reportData.details.map((detail, index) => (
                <React.Fragment key={detail.accountId || index}>
                  <tr className="row-account-summary">
                    <td>
                      <button onClick={() => handleAccountLinkClick(detail.accountId, detail.account)}
                        className="btn-link-account">
                        {detail.account}
                      </button>
                    </td>
                    <td className="text-right"><strong>£{detail.sales.toFixed(2)}</strong></td>
                    <td className="text-right"><strong>{detail.percentage}</strong></td>
                  </tr>
                  {detail.invoices.map((invoice, idx) => (
                    <tr key={`${detail.accountId}-${idx}`}>
                      <td className="cell-invoice-detail">
                        <button onClick={() => handleInvoiceLinkClick(invoice.invoiceNumber)}
                          className="btn-link-invoice">
                          Invoice #{invoice.invoiceNumber}
                        </button>
                        <button onClick={() => handleCaseLinkClick(invoice.caseId)}
                          className="btn-link-case">
                          Case #{invoice.caseId}
                        </button>
                        ({formatDate(invoice.date)})
                      </td>
                      <td className="text-right">£{invoice.amount.toFixed(2)}</td>
                      <td className="text-right">{invoice.percentage}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default SalesReport;